import { zeroAddress } from 'viem'
import { MorphoGeneralPublicResponse } from '../../../types/lender/morpho-types'
import { AdditionalYields, GenericTokenList } from '../../../types'
import { createMarketUid } from '../../../utils'
import { toGenericPriceKey, toOracleKey } from '../../../assets'
import type { CurvanceMarketsRaw, CurvanceTokenRaw } from './types'

/**
 * Synthesized per-market lender key, e.g. `CURVANCE_143_4B0A39EC…`. The chain id
 * rides in the key (the Fluid / River / Frankencoin / Resupply convention) even
 * though Curvance is Monad-only today.
 *
 * Keyed on the MARKET MANAGER, not on a cToken: a Curvance market is the PAIR,
 * both legs share one risk domain (one account liquidity, one hold period, one
 * liquidation), and the pair is immutable once listed. Keying per-cToken would
 * split a single position across two lender keys.
 */
export function curvanceLenderKey(
  lender: string,
  chainId: string | number,
  marketManager: string,
): string {
  return `${lender}_${chainId}_${marketManager.replace(/^0x/i, '').toUpperCase()}`
}

/** Recover `{ lender, chainId, marketManager }` from a per-market key. */
export function curvanceKeyParts(
  key: string,
): { lender: string; chainId: string; marketManager: string } | undefined {
  if (!key.startsWith('CURVANCE_')) return undefined
  const suffix = key.slice('CURVANCE_'.length)
  const m = suffix.match(/^(\d+)_([0-9A-Fa-f]{40})$/)
  if (!m) return undefined
  return {
    lender: 'CURVANCE',
    chainId: m[1],
    marketManager: `0x${m[2].toLowerCase()}`,
  }
}

const SECONDS_PER_YEAR = 31_536_000
const BPS = 10_000
const WAD = 10n ** 18n

/** 1e18-scaled per-second rate → nominal APR in percent (package convention). */
function ratePerSecToApr(rate: bigint): number {
  return (Number(rate) / 1e18) * SECONDS_PER_YEAR * 100
}

function currencyFor(
  address: string,
  decimals: number,
  symbol: string,
  name: string,
  tokens: GenericTokenList,
): any {
  const lower = address.toLowerCase()
  return tokens[lower] ?? { address: lower, symbol, name, decimals }
}

const toFloat = (raw: bigint, decimals: number) => Number(raw) / 10 ** decimals

/**
 * Map one Curvance deployment's on-chain batch into the shared
 * `MorphoGeneralPublicResponse` shape, keyed `CURVANCE_<chainId>_<MARKET>`.
 *
 * Modelling decisions worth knowing, all from CURVANCE.md:
 *
 *  - **`debtCap == 0` is what makes a leg collateral-only.** `isBorrowable()`
 *    returns `true` on every live cToken because they are all
 *    `BorrowableCToken` deployments, so it carries no information. A
 *    collateral-only leg is published with `borrowingEnabled: false` and a 0%
 *    supply rate — because it genuinely pays nothing; its yield is the
 *    UNDERLYING's intrinsic yield, which the yields layer attaches separately.
 *  - **Both legs of a market can be collateral AND borrowable.** FIVE markets
 *    have `debtCap > 0` on both (re-counted on-chain 2026-08-07: WMON|AUSD,
 *    WMON|USDC, WBTC|USDC, WETH|USDC, eBTC|WBTC — this was one market at
 *    integration and is growing, so do not treat it as the exception). The
 *    shape handles it: each row carries its own `collateralActive` /
 *    `borrowingEnabled`, and nothing may assume a market has a single
 *    borrow side.
 *  - **All ratios are BPS.** `collRatio` 8400 = 84% LTV. `collReqSoft` /
 *    `collReqHard` are required-collateralisation ratios (10600 = 106%), which
 *    invert to liquidation thresholds `1/1.06 = 94.3%`. We publish the INVERTED
 *    form as `collateralFactor` because that is what the rest of the stack
 *    means by it.
 *  - **Two liquidation thresholds, not one.** The soft one starts a partial
 *    liquidation at `closeFactorBase` with `liqIncBase`; the hard one is a full
 *    liquidation at `liqIncBase + liqIncCurve`. We publish the SOFT threshold as
 *    the headline (it is where the position first gets touched) and carry both
 *    plus the whole curve in the descriptor.
 *  - **Prices come from the market's own oracle** when the ProtocolReader path
 *    ran, and they are PESSIMISTIC-SPLIT: `assetPriceLower` values collateral,
 *    `assetPrice` values debt. We use the pessimistic side per row rather than
 *    one mid price, matching what the protocol enforces limits with.
 *  - **`isFrozen` means the market cannot be ENTERED, not that it is dead.**
 *    Curvance pauses per action, and exits stay open by design so users can
 *    close. A mint-paused leg is `depositsEnabled: false` but still
 *    `collateralActive`.
 */
export function convertCurvanceMarketsToResponse(
  raw: CurvanceMarketsRaw,
  chainId: string,
  prices: { [asset: string]: number } = {},
  _additionalYields: AdditionalYields = {
    intrinsicYields: {},
    lenderRewards: {},
    loaded: true,
  },
  tokens: GenericTokenList = {},
): { [m: string]: MorphoGeneralPublicResponse } {
  const out: { [m: string]: MorphoGeneralPublicResponse } = {}
  const cfg = raw?.config
  if (!cfg) return out

  for (const market of raw.markets ?? []) {
    if (!market.tokens || market.tokens.length === 0) continue
    const lenderKey = curvanceLenderKey(
      raw.lender,
      chainId,
      market.marketManager,
    )

    // @ts-ignore — built incrementally, matches the Resupply/River converters
    const entry: MorphoGeneralPublicResponse = { data: {} }
    const descriptors: any[] = []
    let published = 0

    for (const t of market.tokens) {
      const assetAddr = t.asset.toLowerCase()
      if (assetAddr === zeroAddress) continue

      const token = tokens[assetAddr]
      const priceKey =
        toOracleKey(token?.assetGroup!) || toGenericPriceKey(assetAddr, chainId)

      // The market's OWN oracle first (pessimistic side per role), then the
      // shared price map. A zero from the reader means "unknown" — the
      // multicall fallback path cannot read prices at all — so it must fall
      // through rather than publish a $0 market.
      const collPrice =
        t.assetPriceLower > 0n
          ? Number(t.assetPriceLower) / 1e18
          : prices[priceKey] || 0
      const debtPrice =
        t.assetPrice > 0n ? Number(t.assetPrice) / 1e18 : prices[priceKey] || 0

      const dec = t.assetDecimals
      // `totalAssets` includes the debt out; `liquidity` is the idle balance.
      const totalDeposits = toFloat(t.totalAssets, dec)
      const totalDebt = toFloat(t.debt, dec)
      const liquidity = toFloat(t.liquidity, dec)

      // Borrowability is the CAP, never `isBorrowable()`.
      const borrowable = t.debtCap > 0n
      const debtHeadroom = borrowable
        ? Math.max(toFloat(t.debtCap, dec) - totalDebt, 0)
        : 0
      // What can actually be drawn: the smaller of idle cash and cap headroom.
      const borrowLiquidity = borrowable ? Math.min(liquidity, debtHeadroom) : 0

      // Collateralisable only when BOTH an LTV and a cap are set — the
      // MarketManager only lets a cap rise above zero once `collRatio > 0`, so
      // requiring both is the protocol's own invariant rather than a guess.
      const collateralActive = t.collRatio > 0n && t.collateralCap > 0n
      const ltv = Number(t.collRatio) / BPS
      // `collReqSoft`/`collReqHard` are REQUIRED COLLATERALISATION (106% ⇒
      // liquidate below 1/1.06). Invert to the liquidation-threshold form the
      // rest of the stack uses.
      const liqThresholdSoft =
        t.collReqSoft > 0n ? BPS / Number(t.collReqSoft) : 0
      const liqThresholdHard =
        t.collReqHard > 0n ? BPS / Number(t.collReqHard) : 0
      // Penalty at the SOFT threshold; it scales up to base+curve at the hard
      // one. `liqIncBase` is a collateralisation ratio too (10250 = 2.5%).
      const liquidationPenalty =
        t.liqIncBase > BigInt(BPS) ? Number(t.liqIncBase) / BPS - 1 : 0
      const liquidationPenaltyMax =
        t.liqIncBase + t.liqIncCurve > BigInt(BPS)
          ? Number(t.liqIncBase + t.liqIncCurve) / BPS - 1
          : liquidationPenalty
      const closeFactor =
        t.closeFactorBase > 0n ? Number(t.closeFactorBase) / BPS : 1

      const borrowApr = ratePerSecToApr(t.borrowRate)
      const supplyApr = ratePerSecToApr(t.supplyRate)
      const utilization = Number(t.utilizationRate) / 1e18

      // A borrow can be stopped three ways, and they are NOT the same thing:
      // an explicit pause, a cap that is already full, and an oracle error
      // (which we cannot see from here — the user-data path surfaces it).
      const borrowHalted = !borrowable || t.borrowPaused || debtHeadroom <= 0
      const depositsHalted = t.mintPaused

      const uid = createMarketUid(chainId, lenderKey, assetAddr)
      entry.data[uid] = {
        marketUid: uid,
        name: t.assetSymbol,
        poolId: market.marketManager.toLowerCase(),
        underlying: assetAddr,
        asset: currencyFor(assetAddr, dec, t.assetSymbol, t.assetName, tokens),
        totalDeposits,
        totalDebtStable: 0,
        totalDebt,
        totalLiquidity: liquidity,
        borrowLiquidity,
        totalLiquidityUSD: liquidity * collPrice,
        borrowLiquidityUSD: borrowLiquidity * debtPrice,
        totalDepositsUSD: totalDeposits * collPrice,
        totalDebtStableUSD: 0,
        totalDebtUSD: totalDebt * debtPrice,
        utilization,
        // A collateral-only leg genuinely pays 0% — there is no lender side on
        // it. Its return is the underlying's own yield, joined in elsewhere.
        depositRate: borrowable ? supplyApr : 0,
        variableBorrowRate: borrowable ? borrowApr : 0,
        stableBorrowRate: 0,
        rateModel: 'curvance-dynamic',
        intrinsicYield: 0,
        rewards: undefined,
        decimals: dec,
        config: {
          0: {
            category: 0,
            borrowCollateralFactor: ltv,
            // The SOFT threshold — where the position first becomes
            // liquidatable, which is the number a risk display must show.
            collateralFactor: liqThresholdSoft,
            borrowFactor: 1,
            liquidationPenalty,
            closeFactor,
            collateralDisabled: !collateralActive,
            debtDisabled: borrowHalted,
          },
        },
        closeFactor,
        collateralActive,
        borrowingEnabled: !borrowHalted,
        // Deposits stay open on a borrow-paused market so users can top up to
        // avoid liquidation; only the paused action itself is stopped.
        depositsEnabled: !depositsHalted,
        hasStable: false,
        variableBorrowDisabled: borrowHalted,
        isActive: !(depositsHalted && borrowHalted),
        isFrozen: depositsHalted && borrowHalted,
      } as any
      published++

      descriptors.push({
        cToken: t.cToken,
        underlying: assetAddr,
        symbol: t.symbol,
        assetSymbol: t.assetSymbol,
        decimals: t.decimals,
        assetDecimals: dec,
        /** THE borrowability gate — never `isBorrowable()`. */
        borrowable,
        debtCap: t.debtCap.toString(),
        collateralCap: t.collateralCap.toString(),
        /** BPS, verbatim, so a caller can reproduce the protocol's own math. */
        collRatioBps: t.collRatio.toString(),
        collReqSoftBps: t.collReqSoft.toString(),
        collReqHardBps: t.collReqHard.toString(),
        liqIncBaseBps: t.liqIncBase.toString(),
        liqIncCurveBps: t.liqIncCurve.toString(),
        closeFactorBaseBps: t.closeFactorBase.toString(),
        /** Inverted to liquidation-threshold form for convenience. */
        liquidationThresholdSoft: liqThresholdSoft,
        liquidationThresholdHard: liqThresholdHard,
        liquidationPenaltySoft: liquidationPenalty,
        liquidationPenaltyHard: liquidationPenaltyMax,
        maxLeverage: t.maxLeverage.toString(),
        mintPaused: t.mintPaused,
        collateralizationPaused: t.collateralizationPaused,
        borrowPaused: t.borrowPaused,
        redeemPaused: t.redeemPaused,
        /** Per-second WAD, as stored. Annualise nominally. */
        borrowRatePerSecond: t.borrowRate.toString(),
        /** One 600 s adjustment period ahead — the vertex multiplier moves. */
        predictedBorrowRatePerSecond: t.predictedBorrowRate.toString(),
        supplyRatePerSecond: t.supplyRate.toString(),
        interestFeeBps: t.interestFee.toString(),
        irmTargetRate: t.irmTargetRate.toString(),
        irmMaxRate: t.irmMaxRate.toString(),
        irmTargetUtilization: t.irmTargetUtilization.toString(),
        /** WAD USD from Curvance's own dual oracle, pessimistic both ways. */
        assetPrice: t.assetPrice.toString(),
        assetPriceLower: t.assetPriceLower.toString(),
      })
    }

    if (published === 0) continue

    const collLeg = market.tokens.find((t) => t.debtCap === 0n)
    const debtLeg = market.tokens.find((t) => t.debtCap > 0n)
    const positionManagers = resolvePositionManagers(cfg, market.marketManager)

    entry.params = {
      market: {
        lender: lenderKey,
        name: marketName(market.tokens),
        loanDecimals: debtLeg?.assetDecimals ?? 18,
        collateralDecimals: collLeg?.assetDecimals ?? 18,
        id: market.marketManager.toLowerCase(),
        lltv: (collLeg ?? market.tokens[0]).collRatio.toString(),
        // Pricing is the shared per-chain OracleManager, not a per-market feed.
        oracle: cfg.oracleManager ?? zeroAddress,
        irm: zeroAddress,
        collateralAddress: (collLeg ?? market.tokens[0]).asset.toLowerCase(),
        loanAddress: (debtLeg ?? market.tokens[0]).asset.toLowerCase(),
        // --- Curvance descriptor (consumed by the calldata builders, the
        // worker-api resolvers and the loop quoter) ---
        curvance: {
          marketManager: market.marketManager,
          tokens: descriptors,
          /**
           * `MIN_HOLD_PERIOD` in seconds. Stamped per account per market by
           * POSTING COLLATERAL and by BORROWING, and while it runs REPAYMENT,
           * REDEMPTION and share transfers all revert. So a position cannot be
           * closed, deleveraged or migrated out for 20 minutes after it is
           * opened or topped up — gate every exit flow on it.
           */
          holdPeriodSeconds: Number(market.cooldownLength || 1200n),
          /**
           * `MIN_LOAN_SIZE` in WAD **USD** — not base units, unlike every
           * other lender's floor. Gates BOTH ends: opening below it reverts,
           * and so does a partial repay leaving less (a full repay always
           * works). `0` means the read failed, NOT that there is no floor.
           */
          minLoanSizeUsdWad: market.minLoanSizeUsdWad.toString(),
          /**
           * Native-leverage entry points for this market, or `undefined` when
           * none is registered — which is a REAL state, not a gap: 5 of the 25
           * live Monad markets have no Position Manager, including the largest
           * one. `undefined` here means no one-click loop on this market.
           */
          positionManagers,
          addresses: {
            centralRegistry: cfg.centralRegistry,
            oracleManager: cfg.oracleManager ?? undefined,
            protocolReader: cfg.protocolReader ?? undefined,
          },
          /**
           * Swap targets with a registered calldata checker. The native
           * leverage path REJECTS any other target, and the mapping is
           * DAO-mutable in both directions, so treat this as a hint and probe
           * `externalCalldataChecker(target)` live before encoding.
           */
          swapTargets: cfg.swapTargets ?? undefined,
        },
      } as any,
    }
    entry.chainId = chainId

    out[lenderKey] = entry
  }

  return out
}

/** `savUSD / AUSD` — collateral leg first, matching how Curvance names markets. */
function marketName(tokens: CurvanceTokenRaw[]): string {
  const coll = tokens.find((t) => t.debtCap === 0n) ?? tokens[0]
  const debt = tokens.find((t) => t !== coll) ?? tokens[tokens.length - 1]
  return `${coll.assetSymbol} / ${debt.assetSymbol}`
}

function resolvePositionManagers(
  cfg: NonNullable<CurvanceMarketsRaw['config']>,
  marketManager: string,
): Record<string, string> | undefined {
  const map = cfg.positionManagers
  if (!map) return undefined
  const direct = map[marketManager]
  if (direct) return { ...direct }
  const target = marketManager.toLowerCase()
  for (const key of Object.keys(map)) {
    if (key.toLowerCase() === target) return { ...map[key] }
  }
  return undefined
}
